/**
 * wall-art-cart-display.js
 *
 * Swaps the natively-rendered cart line item product name + image to show the
 * actual design the customer picked, instead of the Price Map product the
 * line item references.
 *
 * Customer flow recap:
 *   - Customer adds to cart on a design product page → wall-art-picker.js
 *     submits a Price Map variant id (one of the 4 internal pricing helper
 *     products) plus line item properties: _design_id, _design_handle,
 *     _design_image, _design_name.
 *   - Shopify cart endpoint creates a line item for the Price Map variant.
 *   - Cart sections render with the Price Map product's title + image.
 *   - This script scans the rendered DOM, finds line items carrying our
 *     design metadata as data-* attributes (stamped by the cart Liquid
 *     templates — Dawn strips properties starting with `_` from the visible
 *     DOM, so we can't read them out of the property list), and swaps the
 *     visible name + image. If the Price Map product has no image (which is
 *     the normal case — we don't set one), we inject one.
 *
 * Re-run safe (idempotent — guarded by data-wall-art-swapped="done").
 */

(function () {
  'use strict';

  const LINE_ITEM_SELECTOR =
    '.cart-item[data-design-name], .cart-item[data-design-image]';
  const NAME_SELECTOR =
    '.cart-item__name, .cart-notification-product__name';
  const IMG_SELECTOR =
    '.cart-item__image, .cart-notification-product__image img';
  // Where to inject an <img> when the Price Map product has no product image.
  const IMG_HOST_SELECTOR_FULL_CART = 'td.cart-item__media';
  const IMG_HOST_SELECTOR_DRAWER = 'td.cart-item__media';
  const IMG_HOST_SELECTOR_NOTIFICATION = '.cart-item';

  function applyOverridesToLine(lineEl) {
    if (!lineEl || lineEl.dataset.wallArtSwapped === 'done') return;

    const designName = lineEl.dataset.designName || null;
    const designImage = lineEl.dataset.designImage || null;
    const designHandle = lineEl.dataset.designHandle || null;
    if (!designName && !designImage) return;

    // Name swap.
    if (designName) {
      const nameEl = lineEl.querySelector(NAME_SELECTOR);
      if (nameEl) {
        nameEl.textContent = designName;
        if (designHandle && nameEl.tagName === 'A') {
          nameEl.setAttribute('href', `/products/${designHandle}`);
        }
      }
    }

    // Image swap (or inject if missing).
    if (designImage) {
      let imgEl = lineEl.querySelector(IMG_SELECTOR);
      if (!imgEl) {
        imgEl = injectImage(lineEl);
      }
      if (imgEl) {
        imgEl.setAttribute('src', designImage);
        if (imgEl.hasAttribute('srcset')) imgEl.removeAttribute('srcset');
        const picture = imgEl.closest('picture');
        if (picture) {
          for (const source of picture.querySelectorAll('source')) source.remove();
        }
        if (designName) imgEl.setAttribute('alt', designName);
      }
    }

    lineEl.dataset.wallArtSwapped = 'done';
  }

  /** Build a minimal <img> inside the line's media cell when none was rendered
   *  by Liquid (Price Map product had no item.image). Picks the host based on
   *  whatever cell exists. Returns the new <img> or null if no host found. */
  function injectImage(lineEl) {
    const host =
      lineEl.querySelector(IMG_HOST_SELECTOR_FULL_CART) ||
      lineEl.querySelector(IMG_HOST_SELECTOR_DRAWER) ||
      lineEl.querySelector('.cart-notification-product__image') ||
      lineEl;
    if (!host) return null;

    // Full-cart / drawer cells expect the image inside a wrapper div.
    let imgWrapper;
    if (host.matches('td.cart-item__media')) {
      // Match Dawn's structure for proper styling.
      const link = document.createElement('a');
      link.className = 'cart-item__link';
      link.setAttribute('aria-hidden', 'true');
      link.setAttribute('tabindex', '-1');
      link.textContent = ' ';
      const designHandle = lineEl.dataset.designHandle;
      if (designHandle) link.href = `/products/${designHandle}`;
      host.appendChild(link);

      imgWrapper = document.createElement('div');
      imgWrapper.className = 'cart-item__image-container gradient global-media-settings';
      host.appendChild(imgWrapper);
    } else if (host.classList.contains('cart-notification-product__image')) {
      imgWrapper = host;
    } else if (host.matches('.cart-item') && !host.querySelector('.cart-notification-product__image')) {
      // Toast: notification wrapper without image div — inject one.
      imgWrapper = document.createElement('div');
      imgWrapper.className = 'cart-notification-product__image global-media-settings';
      host.insertBefore(imgWrapper, host.firstChild);
    } else {
      imgWrapper = host;
    }

    const img = document.createElement('img');
    img.className = 'cart-item__image';
    img.setAttribute('loading', 'lazy');
    img.setAttribute('width', '150');
    img.setAttribute('height', '150');
    imgWrapper.appendChild(img);
    return img;
  }

  function applyOverridesToAll(root) {
    const scope = root || document;
    const lines = scope.querySelectorAll(LINE_ITEM_SELECTOR);
    for (const line of lines) applyOverridesToLine(line);
  }

  // Re-scan helper that retries a few times because Dawn renders sections
  // asynchronously after add-to-cart — the toast HTML may be injected on a
  // later microtask than our cartUpdate handler fires.
  function applyOverridesWithRetry() {
    let attempts = 0;
    const tick = () => {
      applyOverridesToAll();
      attempts += 1;
      if (attempts < 5) setTimeout(tick, 80);
    };
    tick();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyOverridesToAll());
  } else {
    applyOverridesToAll();
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => applyOverridesWithRetry());
  }

  // MutationObserver across all cart hosts — catches re-renders that don't
  // fire the pub-sub (notably cart-notification in some Dawn versions).
  if (typeof MutationObserver !== 'undefined') {
    const obs = new MutationObserver((mutations) => {
      // If any added node IS a cart-item (or contains one) with our data
      // attrs, run an override pass.
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches?.(LINE_ITEM_SELECTOR) || node.querySelector?.(LINE_ITEM_SELECTOR)) {
            applyOverridesToAll();
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
})();
